/* eslint-disable max-lines */

import { CustomReport, SavedSearch, User } from '@litlingo/client';
import { deleteCustomReport, setUserReport, upsertCustomReport } from 'actions';
import EmptyTableState from 'components/EmptyTableState';
import SelectRedesign from 'components/Select/SelectRedesign';
import UserSelect from 'components/User/UserSelect';
import useClickOutside from 'components/utils/useClickOutside';
import { PLUS_ICON } from 'constants/envelopeIcons';
import React, { useEffect, useRef, useState } from 'react';
import { useDispatch } from 'react-redux';
import { getUserReport } from 'selectors/customReports';
import { useSelector } from 'store';

type DigestUserRowProps = {
  reviewStream: SavedSearch;
  user: User;
  handleDeleteUser?: (user: User) => void;
  allowDelete?: boolean;
};

const DigestUserRow: React.FC<DigestUserRowProps> = ({
  reviewStream,
  handleDeleteUser,
  user,
  allowDelete = false,
}) => {
  const dispatch = useDispatch();
  const userDigest = useSelector(getUserReport(user.uuid));

  const getDigest = (userId: string): CustomReport | null => {
    const customReports = reviewStream.custom_reports || [];

    const report = customReports.find(
      (cReport) => cReport.user_uuid === userId && cReport.kind.trim() !== ''
    );

    if (report) {
      return report;
    }
    return null;
  };

  useEffect(() => {
    if (getDigest(user.uuid)) {
      dispatch(setUserReport({ report: getDigest(user.uuid), delete: false }));
    } else {
      dispatch(setUserReport({ report: userDigest, delete: true }));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [reviewStream.uuid]);

  const handleDigestTypeChange = (userId: string, type: string): void => {
    if (userDigest) {
      if (type === 'null') {
        dispatch(deleteCustomReport({ uuid: userDigest.uuid }));
        dispatch(setUserReport({ report: userDigest, delete: true }));
        return;
      }

      // eslint-disable-next-line @typescript-eslint/no-unused-vars
      const { config: _config, ...rest } = userDigest;
      dispatch(upsertCustomReport({ ...rest, kind: type }));
    } else {
      dispatch(
        upsertCustomReport({
          kind: type,
          user_uuid: userId,
          saved_search_uuid: reviewStream.uuid,
        })
      );
    }
  };

  const renderDigest = (userId: string): JSX.Element => {
    const options = [
      {
        label: 'Daily',
        value: 'daily',
      },
      {
        label: 'Weekly',
        value: 'weekly',
      },
    ];

    if (userDigest) {
      options.push({
        label: '-',
        value: 'null',
      });
    }

    return (
      <div className="text-body">
        <SelectRedesign
          className="bg-white border-none max-h-6"
          optionClassname="bg-white"
          placeholder="-"
          options={options}
          onChange={(type: string): void => handleDigestTypeChange(userId, type)}
          value={userDigest?.kind || ''}
        />
      </div>
    );
  };

  if (!user) return null;

  return (
    <tr key={user.uuid}>
      {allowDelete && (
        <td className="table-wrapper__new-td">
          <button
            type="button"
            className="table-wrapper__new-cell-content h-9 pl-0 text-body text-left focus:outline-none truncate"
            onClick={(): void => handleDeleteUser && handleDeleteUser(user)}
          >
            <svg
              width="20"
              height="20"
              viewBox="0 0 20 20"
              fill="none"
              xmlns="http://www.w3.org/2000/svg"
            >
              <path
                d="M10 20C15.5229 20 20 15.5229 20 10C20 4.47716 15.5229 0 10 0C4.47716 0 0 4.47716 0 10C0 15.5229 4.47716 20 10 20Z"
                fill="#ADADAD"
              />
              <path
                d="M6.40845 6.41016L13.59 13.5917"
                stroke="white"
                strokeLinecap="round"
                strokeLinejoin="round"
              />
              <path
                d="M13.59 6.41016L6.40845 13.5917"
                stroke="white"
                strokeLinecap="round"
                strokeLinejoin="round"
              />
            </svg>
          </button>
        </td>
      )}
      <td className="table-wrapper__new-td pl-2">
        <div className="table-wrapper__new-cell-content h-9 pl-0 text-body text-left truncate">
          {user.name}
        </div>
      </td>
      <td className="table-wrapper__new-cell-content h-9 m-0 pl-0 overflow-visible">
        {renderDigest(user.uuid)}
      </td>
    </tr>
  );
};

type DigestUserCountingRowProps = {
  user: { user: User; count: number };
};

const DigestUserCountingRow: React.FC<DigestUserCountingRowProps> = ({ user }) => {
  if (!user) return null;

  return (
    <tr key={user.user.uuid}>
      <td className="table-wrapper__new-td pl-2">
        <div className="table-wrapper__new-cell-content h-9 pl-0 text-body text-left truncate">
          {user.user.name}
        </div>
      </td>
      <td className="table-wrapper__new-td pl-2">
        <div className="table-wrapper__new-cell-content h-9 pl-0 text-body text-left truncate">
          {user.count}
        </div>
      </td>
    </tr>
  );
};

type DigestUserTableProps = {
  reviewStream: SavedSearch;
  handleSelectUser: (user: User, checked: boolean) => void;
  users: User[];
  handleDeleteUser?: (user: User) => void;
  allowDelete?: boolean;
};

const DigestUserTable: React.FC<DigestUserTableProps> = ({
  reviewStream,
  handleDeleteUser,
  users,
  handleSelectUser,
  allowDelete = false,
}) => {
  const [showUserSelect, setShowUserSelect] = useState(false);

  const usersRef = useRef<HTMLDivElement>(null);

  const { children } = reviewStream;
  const hasChildren = children && children.length > 0;

  const handleToggleShowTagGroups = (): void => {
    setShowUserSelect(!showUserSelect);
  };

  useClickOutside(usersRef, handleToggleShowTagGroups);

  const renderUsers = (): JSX.Element | JSX.Element[] => {
    if (!hasChildren && users.length > 0) {
      return users.map((user) => (
        <DigestUserRow
          key={user.uuid}
          reviewStream={reviewStream}
          user={user}
          handleDeleteUser={handleDeleteUser}
          allowDelete={allowDelete}
        />
      ));
    }

    if (hasChildren && children.length > 0) {
      const childrenUsers = children.reduce<{ user: User; count: number }[]>((acc, child) => {
        const childUsers = [...acc];
        child.users?.forEach((user) => {
          const userIdx = childUsers.findIndex((us) => us.user.uuid === user.uuid);
          if (userIdx !== -1) {
            childUsers[userIdx].count += 1;
          } else {
            childUsers.push({ user, count: 1 });
          }
        });

        return childUsers;
      }, []);

      if (childrenUsers.length === 0) {
        return <EmptyTableState message="No reviewers in this Review Set" colCount="2" />;
      }

      return childrenUsers.map((user) => (
        <DigestUserCountingRow key={user.user.uuid} user={user} />
      ));
    }

    return <EmptyTableState message="No reviewers in this Review Set" colCount="2" />;
  };

  return (
    <div className="flex flex-col gap-2 text-body">
      <div className="flex flex-row justify-between items-center">
        <span className="font-bold">Assigned Reviewers</span>
        <div className="relative flex flex-col">
          {reviewStream.category !== 'sample' &&
            (reviewStream.children?.length === 0 || !!reviewStream.parent_uuid) && (
              <button
                type="button"
                className="px-1 py-0.5 w-25 flex flex-row justify-between items-center gap-1 bg-litlingo-gray-0.5 border border-litlingo-gray-3 rounded text-small focus:outline-none"
                onClick={handleToggleShowTagGroups}
              >
                <span className="w-3 h-3">{PLUS_ICON}</span>
                <span>Edit reviewer</span>
              </button>
            )}
          {showUserSelect && (
            <div className="absolute top-full right-0 mt-1 z-50 rounded" ref={usersRef}>
              <UserSelect
                selectUser={handleSelectUser}
                selectedUsers={users}
                className="min-w-44"
                roles={['reviewer', 'super-reviewer', 'legal-user']}
                defaultOpened
              />
            </div>
          )}
        </div>
      </div>
      <div className="table-wrapper__new no-scrollbar overflow-visible">
        <table className="table-wrapper__table table-wrapper_table--fixed">
          <thead className="sticky top-0 z-10">
            <tr className="table-wrapper__header-tr h-10">
              {allowDelete && (
                <th aria-hidden className="table-wrapper__new-th table-wrapper__th--w-2" />
              )}
              <th className="table-wrapper__new-th table-wrapper__th--w-20 p-0 pl-4 text-left">
                Name
              </th>
              {hasChildren ? (
                <th className="table-wrapper__new-th table-wrapper__th--w-7 p-0 text-left">
                  Assigned In
                </th>
              ) : (
                <th className="table-wrapper__new-th table-wrapper__th--w-7 p-0 text-left">
                  Digest
                </th>
              )}
            </tr>
          </thead>
          <tbody>{renderUsers()}</tbody>
        </table>
      </div>
    </div>
  );
};

export default DigestUserTable;
